import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, Grid3X3, List } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { CIItem } from '../types';
import { ciTypes, getCategories, searchCITypes } from './ITIcons';

interface CategoryValidationStats {
  [category: string]: {
    total: number;
    validated: number;
    pending: number;
    error: number;
  };
}

interface CIPaletteProps {
  selectedNode: CIItem | null;
  categoryValidationStats?: CategoryValidationStats;
}

const CIPalette: React.FC<CIPaletteProps> = ({ selectedNode, categoryValidationStats = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const allCategories = useMemo(() => ['all', ...getCategories()], []);
  
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => new Set()
  );

  // Function to get mini indicator color and validation text
  const getValidationIndicator = (category: string) => {
    const stats = categoryValidationStats[category];
    if (!stats || stats.total === 0) {
      return { color: 'bg-gray-300', tooltipText: 'No items' };
    }

    if (stats.error > 0) {
      return {
        color: 'bg-red-500',
        tooltipText: `${stats.error} error, ${stats.validated}/${stats.total} validated`,
      };
    }

    if (stats.validated === stats.total) {
      return {
        color: 'bg-green-500',
        tooltipText: 'All validated',
      };
    }

    if (stats.validated > 0) {
      return {
        color: 'bg-yellow-500',
        tooltipText: `${stats.validated}/${stats.total} validated`,
      };
    }

    return {
      color: 'bg-slate-400',
      tooltipText: `${stats.pending} pending`,
    };
  };

  const groupedCITypes = useMemo(() => {
    const searchResult = searchCITypes(searchTerm);
    const categoryFiltered = selectedCategory === 'all'
      ? searchResult
      : searchResult.filter(ci => ci.category === selectedCategory);

    return categoryFiltered.reduce((acc, ciType) => {
      (acc[ciType.category] = acc[ciType.category] || []).push(ciType);
      return acc;
    }, {} as Record<string, typeof ciTypes>);
  }, [searchTerm, selectedCategory]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) newSet.delete(category);
      else newSet.add(category);
      return newSet;
    });
  };

  const handleDragStart = (e: React.DragEvent, ciType: { type: string; label: string }) => {
    e.dataTransfer.setData('application/x-service-mapper-ci', JSON.stringify(ciType));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="h-full flex flex-col bg-white" data-ci-palette>
      {/* Header - Light Mode with Chevron styling */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h2 className="text-base font-semibold text-[#0054A4] mb-2">
          Components
        </h2>
        
        {/* Search with IntelliView styling */}
        <div className="relative mb-2">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
          <Input
            placeholder="Search CI templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-xs"
          />
        </div>
        
        {/* Category Filter - IntelliView style */}
        <div className="mb-2">
          <label className="text-2xs font-medium text-gray-600 mb-0.5 block">
            Filter by Category
          </label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {allCategories.map(category => (
                <SelectItem key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* View Toggle - IntelliView style */}
        <div className="flex items-center justify-between">
          <span className="text-2xs font-medium text-gray-600">
            View Mode
          </span>
          <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'list' | 'grid')} className="w-auto">
            <TabsList className="h-7">
              <TabsTrigger value="list" className="h-6 px-2 text-2xs">
                <List className="h-3 w-3 mr-1" />
                List
              </TabsTrigger>
              <TabsTrigger value="grid" className="h-6 px-2 text-2xs">
                <Grid3X3 className="h-3 w-3 mr-1" />
                Grid
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-auto p-2">

        {Object.entries(groupedCITypes).map(([category, types]) => (
          <div key={category} className="mb-2">
            {/* Category Header - Light mode with Chevron blue accent */}
            <div
              className="px-3 py-2 bg-gray-50 border-l-4 border-l-[#0054A4] cursor-pointer hover:bg-gray-100 transition-all duration-200 rounded-r-md shadow-sm group"
              onClick={() => toggleCategory(category)}
              title={categoryValidationStats[category] ? getValidationIndicator(category).tooltipText : ''}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-semibold text-[#1E3A5F]">
                  {category} ({types.length})
                </h3>
                
                {/* Mini validation indicator dot */}
                {categoryValidationStats[category] && (
                  <div className={`h-2 w-2 rounded-full flex-shrink-0 ${getValidationIndicator(category).color} opacity-0 group-hover:opacity-100 transition-opacity`} />
                )}
                
                <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                  {expandedCategories.has(category) ? 
                    <ChevronUp className="h-3 w-3" /> : 
                    <ChevronDown className="h-3 w-3" />
                  }
                </Button>
              </div>
            </div>
            
            {/* Category Content */}
            {expandedCategories.has(category) && (
              <div className="px-2 py-2">
                {viewMode === 'list' ? (
                  // List View - Light mode
                  <div className="space-y-0.5">
                    {types.map((ciType, index) => (
                      <Card
                        key={`${ciType.label}-${index}`}
                        className="cursor-grab hover:shadow-lg hover:border-[#0054A4] transition-all duration-200 border border-gray-200 bg-white shadow-sm"
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, ciType)}
                      >
                        <CardContent className="p-2">
                          <div className="flex items-center space-x-2">
                            <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center" style={{ color: ciType.color }}>
                              {ciType.icon}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xs font-medium text-gray-900">
                                {ciType.label}
                              </h4>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  // Grid View - Light mode
                  <div className="grid grid-cols-2 gap-1">
                    {types.map((ciType, index) => (
                      <Card
                        key={`${ciType.label}-${index}`}
                        className="cursor-grab hover:shadow-lg hover:border-[#0054A4] transition-all duration-200 border border-gray-200 bg-white shadow-sm text-center"
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, ciType)}
                      >
                        <CardContent className="p-2">
                          <div className="flex flex-col items-center space-y-1">
                            <div style={{ color: ciType.color }}>
                              {React.cloneElement(ciType.icon, { style: { fontSize: 24 } })}
                            </div>
                            <h4 className="text-2xs font-medium text-gray-900 text-center leading-tight">
                              {ciType.label}
                            </h4>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        
        {Object.keys(groupedCITypes).length === 0 && (
          <div className="p-6 text-center">
            <p className="text-sm text-gray-500">
              No CI types found matching your search.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CIPalette;